import { memo, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Layers, Plus } from 'lucide-react';
import { useResaltado } from './ValidadorPanel';
import { Badge, type BadgeVariant } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ApiError } from '../../lib/api';
import {
  composicionApi,
  composicionKeys,
  type FaseArbol,
  type HerramientaCatalogo,
  type TipoFase,
} from '../../lib/composicion';
import { TarjetaSesion } from './TarjetaSesion';
import type { OpcionSesion } from './FichaActividad';

/** Consecuencia de cada tipo metodológico (misma microcopy que al crear la fase). */
export const TIPO_FASE_INFO: Record<
  TipoFase,
  { etiqueta: string; variant: BadgeVariant; micro: string }
> = {
  divergente: {
    etiqueta: 'Divergente',
    variant: 'info',
    micro: 'Las ideas de cada quien aún no se proyectan como síntesis.',
  },
  convergente: {
    etiqueta: 'Convergente',
    variant: 'primary',
    micro: 'La sala converge y los agregados se muestran.',
  },
  neutra: {
    etiqueta: 'Neutra',
    variant: 'neutral',
    micro: 'Sin regla especial de proyección: cada herramienta decide qué se muestra.',
  },
};

/**
 * Columna de una fase en el lienzo horizontal: encabezado (nombre + tipo con
 * su consecuencia), sesiones apiladas y «+ Sesión» al pie.
 */
export const ColumnaFase = memo(function ColumnaFase({
  fase,
  procesoId,
  herramientaSeleccionada,
  opcionesSesion,
}: {
  fase: FaseArbol;
  procesoId: string;
  herramientaSeleccionada: HerramientaCatalogo | null;
  opcionesSesion: OpcionSesion[];
}) {
  const qc = useQueryClient();
  const tipo = TIPO_FASE_INFO[fase.tipo];

  // Resaltado del validador (Lote 3): una advertencia que apunta SOLO a la
  // fase (p. ej. «fase sin sesiones») ringea la columna entera.
  const resaltado = useResaltado();
  const resaltada =
    resaltado?.faseId === fase.id && !resaltado?.sesionId && !resaltado?.actividadId;
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (resaltada) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [resaltada]);

  const crearSesionMut = useMutation({
    mutationFn: () => composicionApi.crearSesion(fase.id),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: composicionKeys.arbol(procesoId) }),
  });
  const accionError = (crearSesionMut.error as ApiError | null)?.detail ?? null;

  return (
    <section
      ref={ref}
      className={`flex w-80 shrink-0 flex-col gap-3 rounded-lg transition-shadow duration-base ${
        resaltada ? 'ring-2 ring-warning' : ''
      }`}
    >
      <header className="flex items-center gap-2">
        <Layers className="h-4 w-4 shrink-0 text-content-muted" aria-hidden="true" />
        <h3 className="min-w-0 flex-1 truncate font-display text-base font-semibold text-content">
          {fase.nombre}
        </h3>
        <span title={tipo.micro}>
          <Badge variant={tipo.variant}>{tipo.etiqueta}</Badge>
        </span>
      </header>

      {fase.sesiones.length === 0 && (
        <p className="rounded-md border border-dashed border-border px-3 py-2.5 font-sans text-xs text-content-muted">
          Esta fase todavía no tiene sesiones. Agregá la primera y ponele fecha:
          por ejemplo, la reunión con los COCODEs de la zona 3.
        </p>
      )}

      {fase.sesiones.map((s, i) => (
        <TarjetaSesion
          key={s.id}
          sesion={s}
          indice={i + 1}
          procesoId={procesoId}
          herramientaSeleccionada={herramientaSeleccionada}
          opcionesSesion={opcionesSesion}
        />
      ))}

      <Button
        variant="secondary"
        size="sm"
        fullWidth
        loading={crearSesionMut.isPending}
        onClick={() => crearSesionMut.mutate()}
        leadingIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
      >
        Sesión
      </Button>

      {accionError && (
        <p className="flex items-start gap-1.5 font-sans text-xs text-error">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {accionError}
        </p>
      )}
    </section>
  );
});
